import React, { useEffect, useRef, useState, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useLocation, useHistory, useRouteMatch, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { add, isBefore, format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import _ from 'lodash';

import './RacingDetail.scss';
import { select, RootState } from '../../configs/store';
import { replaceUrls } from '../../api/apiUrls';
import { DetailedRaceEvent, RaceEvent, RaceMarket } from '../../interfaces/RacingState';
import RacingDetailContext from '../../components/HorseBetting/Context/RacingDetail.context';
import { validName } from '../../utils/racingCombinations';
import { selectionTypes } from '../../utils/content';
import Page from '../../containers/Page/Page';
import DetailHeader from '../../components/DetailHeader/DetailHeader';
import SimpleTabs from '../../components/SimpleTabs/SimpleTabs';
import Race from '../../components/HorseBetting/Race/Race';
import EventInfo from '../../components/HorseBetting/EventInfo/EventInfo';
import MarketsContainer from '../../components/HorseBetting/MarketsContainer/MarketsContainer';
import CompetitorsTable from '../../components/HorseBetting/CompetitorsTable/CompetitorsTable';
import SearchResults from '../../components/SearchResults/SearchResults';
import Button from '../../components/Button/Button';
import RaceSummary from 'src/components/HorseBetting/RaceSummary/RaceSummary';
import Summary from 'src/components/Summary/Summary';
import Accordion from '../../components/Accordion/Accordion';
import QuickBet from 'src/components/Betslip/QuickBet/QuickBet';
import FinishedRace from 'src/components/HorseBetting/FinishedRace/FinishedRace';
import AppModeContext from 'src/containers/AppMode/AppMode.context';
import FastMode from 'src/components/HorseBetting/FastMode/FastMode';
import useIsMobile from 'src/hooks/useIsMobile';

const TAB_TO_NUMBER = {
  'all': 0,
  'fixed-odds': 1,
  'tote': 2
};

function RacingDetail() {
  const [eventFound, setEventFound] = useState(true);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [selectedSelectionType, setSelectedSelectionType] = useState('singlePerm');
  const [selectedHorsePosition, setSelectedHorsePosition] = useState([]);
  const [combinations, setCombinations] = useState();
  const [quickBet, setQuickBet] = useState(false);
  const [showCompetitors, setShowCompetitors] = useState({ show: false, open: 0, total: 0 });
  const [openAll, setOpenAll] = useState(false);
  const [isValidForQuickBet, setIsValidForQuickBet] = useState(false);
  const [official, setOfficial] = useState(false);
  const [podiumNr, setPodiumNr] = useState(0);
  const [date, setDate] = useState(new Date);
  const [raceStatus, setRaceStatus] = useState('');
  const [isReloading, setIsReloading] = useState(false); //if it's reloading prevent combinations from disapearing

  const currentSelectedMarket = useRef('');
  const previousSelectedMarket = useRef('');

  const mobile = useIsMobile();
  const { RETAIL_MODE } = useContext(AppModeContext);
  const { meetingCode, event, tab, market } = useParams<{ meetingCode: string, event: string, tab: 'all' | 'fixed-odds' | 'tote', market: string }>();
  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch();
  const { t } = useTranslation('common');

  const dispatch = useDispatch();
  const baseMarket: RaceMarket = useSelector(select.racing.baseMarket);
  const { name, country, countryCode, type, couponCode, cardCode, events } = useSelector((state: RootState) => state.racing.meeting);
  const { isSearching, isDirty } = useSelector((state: RootState) => state.events);
  const { calendar } = useSelector((state: RootState) => state.racing);
  const { selectedTimezone } = useSelector((state: RootState) => state.odds);
  const { bets } = useSelector((state: RootState) => state.betslip);
  const selectedEvent = useSelector(select.racing.selectedEvent);

  const defaultTab = tab ? TAB_TO_NUMBER[tab] : 0;
  const openEvents = _.filter(events, evt => evt.status === 'OPEN' && evt.markets.length > 0);

  const toteMarketsCount = _.reduce(events, (total, evt) => {
    return total += _.filter(evt.markets, m => m.oddsType === 2).length;
  }, 0);
  const fixedOddsMarketsCount = _.reduce(events, (total, evt) => {
    return total += _.filter(evt.markets, m => m.oddsType === 3).length;
  }, 0);

  let sportIcon = 'horses';
  if (type === 204) sportIcon = 'dogs';
  else if (type === 203) sportIcon = 'carriages';

  const context = {
    selectedMarket,
    setSelectedMarket,
    event: selectedEvent,
    selectedSelectionType,
    setSelectedSelectionType,
    selectedHorsePosition,
    setSelectedHorsePosition,
    combinations,
    setCombinations,
    quickBet,
    setQuickBet,
    showCompetitors,
    setShowCompetitors,
    isValidForQuickBet,
    setIsValidForQuickBet,
    hideDetail: defaultTab !== 0,
    official,
    setOfficial,
    podiumNr,
    setPodiumNr
  };

  //#region FUNCTIONS
  const loadEvent = async (hideLoading = false, shouldReset=false) => {
    setIsReloading(shouldReset);
    const found = await dispatch.racing.getMeetingByCode({ hideLoading, meetingCode, event });

    await dispatch.racing.getMeetingInfo({ meetingCode });

    if (defaultTab !== 0 && events?.length > 0) {
      if (selectedEvent?.meetingCode) {
        await dispatch.racing.getFixedOddsCompetitors({ meeting: selectedEvent.meetingCode });
      }
    }

    if (found !== eventFound) {
      setEventFound(found);
    }
    setIsReloading(false);
  };

  const onTabChange = (e, val: number) => {
    const tabString = _.findKey(TAB_TO_NUMBER, (value) => value === val);

    if (!tab) {
      history.push(`${ location.pathname }/${ tabString }${ location.search }`);
    } else {
      history.push(`${ replaceUrls(match.path, Object.assign(match.params,
        {
          tab: tabString,
          market: tabString === 'fixed-odds' ? baseMarket?.code : match.params['market']
        }
      )) }${ location.search }`);
    }
  };

  const getSelectedOutcomesByRace = (event: RaceEvent | DetailedRaceEvent) => {
    let outcomesByRace = 0;

    if ('markets' in event) {
      for (const market of event.markets) {
        if ('outcomes' in market) {
          for (const outcome of market.outcomes) {
            const betIndex = _.findIndex(bets, {
              eventCode: event.eventCode,
              marketCode: market.code,
              outcome: outcome.name,
              genericName: market.genericName
            });

            if (betIndex !== -1) {
              outcomesByRace += 1;
            }
          }
        }
      }
    }

    return outcomesByRace;
  };

  useEffect(() => {
    if (selectedEvent) {
      setDate(utcToZonedTime(selectedEvent.eventStart * 1000, selectedTimezone));

      if (selectedEvent.status !== 'OFFICIAL'
      && isBefore(utcToZonedTime(selectedEvent.eventStart * 1000, selectedTimezone), add(new Date(), { seconds: 1 }))) {
        setRaceStatus(t('horseDetail.started'));
      } else {
        setRaceStatus('');
      }
    }
  }, [selectedEvent]);

  useEffect(() => {
    if (history.action === 'PUSH' || history.action === 'POP'){
      loadEvent();
    }

    const refreshInterval = setInterval(() => {
      loadEvent(true, true);
    }, 120000);

    return () => {
      clearInterval(refreshInterval);
    };
  }, [meetingCode, event]);

  useEffect(() => {
    if (selectedEvent?.status ==='OFFICIAL'){
      setOfficial(true);
    } else {
      setOfficial(false);
    }

  }, [selectedEvent]);

  useEffect(() => {
    if (selectedEvent && !isReloading) {
      const marketIsNumber = selectedEvent.markets[0]?.code === +selectedEvent.markets[0]?.code;
      const selectedMarketIndex = _.findIndex(selectedEvent.markets, { code: marketIsNumber ? +market : market });
      if (selectedMarketIndex !== -1) {
        setSelectedMarket({ ...selectedEvent.markets[selectedMarketIndex], index: selectedMarketIndex });
      } else if (baseMarket) {
        setSelectedMarket({ ...baseMarket, index: 0 });
      }
    }
  }, [market, selectedEvent]);

  useEffect(() => {
    if (selectedMarket) {
      const isSpecialToteMarket = selectionTypes['singlePerm'][validName(selectedMarket)];
      if (!isSpecialToteMarket){
        setSelectedSelectionType(null);
      }

      if (isSpecialToteMarket || selectedMarket.isCrossEventMarket || isValidForQuickBet){
        const numberOfPositions = isSpecialToteMarket ? selectedMarket.predictionCount + 1 : selectedMarket.predictionCount;
        setSelectedHorsePosition([...Array(numberOfPositions)].map(() => Array(0)));
      }
      setIsValidForQuickBet(selectedMarket.predictionCount > 1);

      previousSelectedMarket.current = currentSelectedMarket.current;
      currentSelectedMarket.current = selectedMarket.name;
    }
  }, [selectedMarket]);

  useEffect(() => {
    if (defaultTab !== 0 && events?.length > 0) {
      if (defaultTab === 1 && selectedEvent) {
        const foMarkets = _.filter(selectedEvent.markets, { oddsType: 3 });

        if (foMarkets.length > 0) {
          setSelectedMarket(foMarkets[2] || foMarkets[1] || foMarkets[0]);
        }
      }
      if (selectedEvent?.meetingCode) {
        dispatch.racing.getFixedOddsCompetitors({ meeting: selectedEvent.meetingCode });
      }

    }
  }, [defaultTab, events?.length]);

  useEffect(() => {
    document.title = `BetXchange - Racing ~ ${ name ?? '' }`;
  }, [name]);

  useEffect(() => {
    // Reset checkBoxes
    if (previousSelectedMarket.current !== currentSelectedMarket.current){
      const isSpecialToteMarket = selectionTypes['singlePerm'][validName(selectedMarket)];
      if (!isSpecialToteMarket){
        setSelectedSelectionType(null);
      } else {
        setSelectedSelectionType('singlePerm');
      }

      if (isSpecialToteMarket || selectedMarket.isCrossEventMarket || selectedMarket.predictionCount){
        let numberOfPositions = isSpecialToteMarket ? selectedMarket.predictionCount + 1 : selectedMarket.predictionCount;

        if (numberOfPositions<=0){
          numberOfPositions = 1;
        }
        setSelectedHorsePosition([...Array(numberOfPositions)].map(() => Array(0)));
      }
    }
  }, [selectedMarket, selectedSelectionType]);

  useEffect(() => {
    let dirty = false;
    //Check if selected events are different from default selected
    if (_.some(selectedHorsePosition, (position) => position.length > 0)) {
      dirty = true;
    }

    if (isDirty !== dirty) {
      dispatch.events.changeDirtiness(dirty);
    }
  }, [selectedHorsePosition]);

  useEffect(() => {
    return () => {
      if (history.action === 'PUSH'){
        dispatch.racing.clearMeeting();
      }
    };
  }, [history]);
  //#endregion

  const setLabels = () => {
    const labels = [
      { label: t('racingDetail.allMarkets'), variant: 'smaller-text' },
      { label: t('racingDetail.quickCharts'), variant: 'smaller-text' },
      { label: t('racingDetail.toteQuickPicks'), variant: 'smaller-text' }
    ];

    return labels;
  };

  return (
    <Page>
      {
        isSearching && !mobile
          ? <SearchResults.Racing />
          : (
            <RacingDetailContext.Provider value={ context }>
              { RETAIL_MODE && <FastMode /> }
              <div className="racing-detail">
                <DetailHeader>
                  <DetailHeader.RacingDetail
                    event={ { countryCode, name, date: date && format(date, 'dd MMM') } }
                    icon={ sportIcon }
                    couponCode={ couponCode }
                    cardCode={ cardCode }
                  />
                </DetailHeader>
                <SimpleTabs
                  labels={ setLabels() }
                  containerClass="racing-detail"
                  showIndicator={ false }
                  defaultTab={ defaultTab }
                  onChange={ onTabChange }
                >
                  <>
                    {
                      events.length > 0  &&
                    <div className="racing-detail__events-container">
                      {

                        _.map(calendar, (evt) => (
                          evt.eventCode.toString() === event ? (
                            <Race
                              key={ evt.eventCode }
                              racingEvent={ Object.assign(evt, { meetingCode: meetingCode }) }
                              meetingCode={ Number(meetingCode) }
                              selected={ true }
                            />
                          ) : (
                            <Race
                              key={ evt.eventCode }
                              racingEvent={ Object.assign(evt, { meetingCode: meetingCode }) }
                              meetingCode={ Number(meetingCode) }
                            />
                          )
                        ))
                      }
                    </div>
                    }
                    {
                      selectedEvent && eventFound && (
                        <>
                          <EventInfo raceStatus={ raceStatus } />

                          {
                            selectedEvent.markets?.length > 0 && selectedEvent.status !=='OFFICIAL' &&
                            <MarketsContainer key={ `MarketsContainer-${ event }` } />
                          }

                          {
                            selectedEvent.competitors?.length > 0 && selectedMarket && selectedEvent.status !=='OFFICIAL' && (
                              <CompetitorsTable
                                key={ `CompetitorsTable-${ event }` }
                                selectedEvent={ selectedEvent }
                                maxMarkets={ selectedMarket.typeCode === 15100 ? 2 : 1 }
                                racingType={ sportIcon }
                              />
                            )
                          }
                          { selectedEvent?.status ==='OFFICIAL' && <FinishedRace evt = { selectedEvent.eventCode } racingType={ sportIcon } /> }
                        </>
                      )
                    }
                    {
                      !eventFound && (
                        <div className="racing-detail__event-not-found">
                          <p>{ t('racingDetail.eventNotFound') }</p>
                          <Button
                            className="btn--outlined"
                            text={ t('racingDetail.tryAgain') }
                            onClick={ loadEvent }
                          />
                        </div>
                      )
                    }
                  </>

                  <>
                    {
                      openEvents.length > 0 && fixedOddsMarketsCount > 0
                        ? (
                          <>
                            <Button
                              className="racing-detail__open-all"
                              onClick={ () => setOpenAll(!openAll) }
                            >
                              { !openAll ? t('racingDetail.openAll') : t('racingDetail.closeAll') }
                            </Button>
                            {
                              _.map(openEvents, evt => {
                                const date = utcToZonedTime(evt.eventStart * 1000, selectedTimezone);

                                return (
                                  <Accordion
                                    key={ `Race ${ evt.raceNo }` }
                                    title={ `Race ${ evt.raceNo } - ${ format(date, 'HH:mm') }` }
                                    defaultOpen={ evt.eventCode.toString() === event }
                                    open={ openAll }
                                    totalSelected={ getSelectedOutcomesByRace(evt) }
                                  >
                                    {
                                      selectedMarket &&
                                    <CompetitorsTable selectedEvent={ evt } maxMarkets={ 3 } racingType={ sportIcon } quickCharts />
                                    }
                                  </Accordion>
                                );
                              })
                            }
                          </>
                        )
                        : (
                          <div className="racing-detail__event-not-found">
                            <p>{ t('racingDetail.noOpenRaces') }</p>
                          </div>
                        )
                    }
                  </>

                  <>
                    {
                      openEvents.length > 0 && toteMarketsCount > 0
                        ? (
                          <>
                            <Button
                              className="racing-detail__open-all"
                              onClick={ () => setOpenAll(!openAll) }
                            >
                              { !openAll ? t('racingDetail.openAll') : t('racingDetail.closeAll') }
                            </Button>
                            {
                              _.map(openEvents, evt => {
                                const date = utcToZonedTime(evt.eventStart * 1000, selectedTimezone);

                                return (
                                  <Accordion
                                    key={ `Race ${ evt.raceNo }` }
                                    title={ `Race ${ evt.raceNo } - ${ format(date, 'HH:mm') }` }
                                    defaultOpen={ evt.eventCode.toString() === event }
                                    open={ openAll }
                                  >
                                    <div className="racing-detail__accordion">
                                      {
                                        _.map(_.filter(evt.markets, m => m.oddsType === 2), market => (
                                          <Link
                                            key={ market.code }
                                            onClick={ () => setSelectedMarket(market) }
                                            to={ `/racing/${ meetingCode }/${ evt?.eventCode }/all/${ market.code }` }
                                            className="racing-detail__btn"
                                          >
                                            <span>{ market.name }</span>
                                          </Link>
                                        ))
                                      }
                                    </div>
                                  </Accordion>
                                );
                              })
                            }
                          </>
                        )
                        : (
                          <div className="racing-detail__event-not-found">
                            <p>{ t('racingDetail.noOpenRaces') }</p>
                          </div>
                        )
                    }
                  </>
                </SimpleTabs>
                {
                  (isValidForQuickBet || (selectedMarket && selectedMarket.oddsType === 2)) && defaultTab === 0 && selectedEvent && (
                    <Summary>
                      <RaceSummary />
                    </Summary>
                  )
                }
                {
                  quickBet && (
                    <QuickBet
                      icon={ sportIcon }
                      country={ country }
                      tournamentName={ `${ name }, R${ selectedEvent.raceNo }` }
                      isRacingBet={ true }
                    />
                  )
                }
              </div>
            </RacingDetailContext.Provider>
          )
      }
    </Page>
  );
}

export default RacingDetail;
